import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import FileButton from './FileButton';
import compareByNoThenSpeciesName from '../utils/compareByNoThenSpeciesName';
import { phylumLabel } from '../utils/phylum';
import uploadFile from '../utils/uploadFile';
// Hoisted rather than redeclared inline in every render method below (was:
// the identical `const t = (k) => ...` in view()/intField()/textField()/
// boolField()/fontFields()/qrHidesField()/logoField()). No explicit return
// type - app.translator.trans(id)'s own declared signature already covers
// exactly what this returns.
function t(key) {
  return app.translator.trans(`champis-exhibitions.forum.card_layout_editor.${key}`);
}

// Every champis_exhibitions_societies card_* column's own value type -
// see CARD_ATTRS' own comment for why this stays a plain string-keyed bag
// rather than a fully-keyed interface.

// Same ordering as CartothequePage's own compareEntries().
const compareEntries = compareByNoThenSpeciesName(entry => entry.no());

// TCPDF's own 3 core Type1 families (case-insensitive) - a small, low-risk
// improvement over mycocarto's own font <select>, which only ever offered
// one hardcoded option ("Helvetica").
const FONTS = ['helvetica', 'times', 'courier'];

// TCPDF's own native style vocabulary ('' / B / I / BI) - not a
// extension-specific convention to reinterpret, so the <select> values are
// exactly these, only the visible labels are translated.
const FONT_STYLES = ['', 'B', 'I', 'BI'];

// Browser-safe stand-ins for TCPDF's own 3 core families - close enough for
// an approximate live preview (see this file's own docblock).
const FONT_STACKS = {
  helvetica: 'Helvetica, Arial, sans-serif',
  times: '"Times New Roman", Times, serif',
  courier: '"Courier New", Courier, monospace'
};

// Exactly the keys CardPdfBuilder::build()'s own $hidden() closure
// understands - one other element a société can pick to skip drawing
// whenever the QR code actually renders on a card, so the QR (draggable
// anywhere, always on top - see that method's own docblock) doesn't just
// have to paint over whatever was already there. 'species_name' covers
// both the canonical name and its author together, same reasoning as that
// closure's own comment - the two are drawn as one unit, hiding just one
// of them isn't a real scenario.
const QR_HIDABLE_ELEMENTS = [['no', 'section_numbering'], ['logo', 'section_logo'], ['society_name', 'section_society_name'], ['family', 'section_family'], ['phylum', 'section_phylum'], ['species_name', 'section_canonical_name'], ['synonym', 'section_synonym'], ['vernacular_name', 'section_vernacular_name'], ['toxicity_text', 'section_toxicity_text'], ['toxicity_logo', 'section_toxicity_logo']];

// Every champis_exhibitions_societies card_* column, flat - initialized
// from and saved back to the Society model by exactly these attribute
// names, so this list alone drives both oninit() and submit(). Every one
// of these names is also a real zero-arg getter on Society itself (see
// that model) - this.fields stays a plain Record<string, any> rather than
// a fully-keyed interface: the whole point of this list is looping over
// Society's own methods by name, which is inherently a dynamic-reflection
// pattern TypeScript's static key-to-value-type correlation can't follow
// through a forEach without real loss of precision either way.
const CARD_ATTRS = ['cardWidth', 'cardHeight', 'cardSocietyName', 'cardSocietyNameX', 'cardSocietyNameY', 'cardSocietyNameSize', 'cardSocietyNameFont', 'cardSocietyNameFontStyle', 'cardLogoUrl', 'cardLogoWidth', 'cardLogoHeight', 'cardLogoX', 'cardLogoY', 'cardShowFamily', 'cardFamilyX', 'cardFamilyY', 'cardFamilySize', 'cardFamilyFont', 'cardFamilyFontStyle', 'cardShowPhylum', 'cardPhylumX', 'cardPhylumY', 'cardPhylumSize', 'cardPhylumFont', 'cardPhylumFontStyle', 'cardSplitCanonicalName', 'cardCanonicalNameX', 'cardCanonicalNameY', 'cardCanonicalNameSize', 'cardCanonicalNameFont', 'cardCanonicalNameFontStyle', 'cardAuthorX', 'cardAuthorY', 'cardAuthorSize', 'cardAuthorFont', 'cardAuthorFontStyle', 'cardShowSynonym', 'cardSynonymX', 'cardSynonymY', 'cardSynonymSize', 'cardSynonymFont', 'cardSynonymFontStyle', 'cardVernacularNameX', 'cardVernacularNameY', 'cardVernacularNameSize', 'cardVernacularNameFont', 'cardVernacularNameFontStyle', 'cardShowToxicityText', 'cardToxicityTextX', 'cardToxicityTextY', 'cardToxicityTextSize', 'cardToxicityTextFont', 'cardToxicityTextFontStyle', 'cardShowToxicityLogo', 'cardToxicityLogoWidth', 'cardToxicityLogoHeight', 'cardToxicityLogoX', 'cardToxicityLogoY', 'cardShowQrCode', 'cardQrCodeSize', 'cardQrCodeX', 'cardQrCodeY', 'cardQrCodeHides', 'cardWithNo',
// Not a card_* column, but this same form's - see Society's own getter.
'cartothequeSortByPhylum'];

// Fallback content for the live preview when no real card is picked from
// the dropdown below (see loadEntries()) - a plausible, fully-populated
// example so every configurable element has *something* to show.
const FICTIONAL_SAMPLE = {
  no: '1a',
  canonicalName: 'Amanita muscaria',
  author: '(L.) Lam.',
  synonymName: 'Agaricus muscarius',
  family: 'Amanitaceae',
  phylum: 'Basidiomycota',
  vernacularName: 'Fausse oronge',
  toxicityName: 'Toxique',
  hasDescription: true
};
/**
 * Configures how a société's cartothèque cards print - card size plus, for
 * each element (société name/logo/family/canonical name/author/vernacular
 * name/toxicity text/toxicity logo), its position/size/font. Consumed
 * server-side by CardPdfBuilder. Lives directly in SocietyPage's own
 * "Mise en page des cartes" block (not a modal, per explicit feedback -
 * the whole point is seeing the effect of a change immediately, which a
 * modal you have to close and re-download a PDF from defeats) - a plain
 * Component, not a Page/Modal, attrs.society required (always edit mode,
 * same reasoning as SocietyEditModal for why this extension has no
 * société Create flow).
 *
 * The preview on the right is an HTML/CSS approximation of what
 * CardPdfBuilder actually renders with TCPDF, not a pixel-perfect one:
 * font rendering differs between a browser and TCPDF, and - the one
 * deliberate behavioral simplification - author/vernacular name are shown
 * at their own configured (x, y) without CardPdfBuilder's own dynamic
 * "push down by however many extra lines the canonical name wrapped onto"
 * adjustment (that logic depends on TCPDF's own line-wrapping measurements,
 * not the browser's) - a long canonical name visibly overlapping author
 * below in this preview is itself a useful signal to widen the gap or
 * enable "split genus/epithet", even without the auto-shift.
 */
export default class CardLayoutEditor extends Component {
  fields = {};
  logoUploading = false;
  loading = false;
  saved = false;
  errorMessage = null;
  entries = null;
  previewEntryId = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.resetFields();
    this.logoUploading = false;
    this.loading = false;
    this.saved = false;
    this.errorMessage = null;

    // Real cards to preview with, in addition to the fictional fallback
    // above - picking an actual card (especially a long name/several
    // synonyms-worth of text) is a much more useful stress-test of a
    // layout than an idealized example ever is.
    this.entries = null;
    this.previewEntryId = null;
    this.loadEntries();
  }

  // Shared by oninit() and the "Annuler" button - the société model
  // itself is only ever mutated by a successful save() (see submit()), so
  // its own card* attributes still hold the last-saved values regardless
  // of whatever unsaved edits sit in this.fields - re-reading them here
  // is a real discard-changes reset, not just "back to page load".
  resetFields() {
    const society = this.attrs.society;
    this.fields = {};
    CARD_ATTRS.forEach(key => {
      this.fields[key] = society[key]();
    });
  }
  loadEntries() {
    const society = this.attrs.society;

    // Toxicities loaded alongside the entries themselves (not just relying
    // on SocietyPage's own toxicitiesSection() having already populated
    // the store first) so entry.toxicity() below resolves regardless of
    // section render order - same reasoning CartothequePage's own
    // load()/loadToxicities() split already established.
    Promise.all([app.store.find('champis-exhibitions-toxicities', {
      filter: {
        society: society.id()
      }
    }), app.store.find('champis-exhibitions-cartotheque-entries', {
      filter: {
        society: society.id()
      },
      page: {
        limit: 2000
      }
    })]).then(_ref => {
      let [, entries] = _ref;
      this.entries = entries.slice().sort(compareEntries);
      m.redraw();
    });
  }
  view() {
    return m("div", {
      className: "ChampisCardLayoutEditor"
    }, this.errorMessage && m("div", {
      className: "ChampisCardLayoutEditor-error"
    }, this.errorMessage), m("div", {
      className: "ChampisCardLayoutEditor-columns"
    }, m("div", {
      className: "ChampisCardLayoutEditor-fields"
    }, this.group(t('section_size'), [this.intField('cardWidth', 'width_label'), this.intField('cardHeight', 'height_label')]), this.group(t('section_society_name'), [this.textField('cardSocietyName', 'text_label', 'text_placeholder'), this.intField('cardSocietyNameX', 'x_label'), this.intField('cardSocietyNameY', 'y_label'), this.intField('cardSocietyNameSize', 'size_label'), this.fontFields('cardSocietyNameFont', 'cardSocietyNameFontStyle')]), this.group(t('section_logo'), [this.logoField(), this.intField('cardLogoWidth', 'width_label'), this.intField('cardLogoHeight', 'height_label'), this.intField('cardLogoX', 'x_label'), this.intField('cardLogoY', 'y_label')]), this.group(t('section_family'), [this.boolField('cardShowFamily', 'show_label'), this.intField('cardFamilyX', 'x_label'), this.intField('cardFamilyY', 'y_label'), this.intField('cardFamilySize', 'size_label'), this.fontFields('cardFamilyFont', 'cardFamilyFontStyle')]), this.group(t('section_phylum'), [this.boolField('cardShowPhylum', 'show_label'), this.intField('cardPhylumX', 'x_label'), this.intField('cardPhylumY', 'y_label'), this.intField('cardPhylumSize', 'size_label'), this.fontFields('cardPhylumFont', 'cardPhylumFontStyle')]), this.group(t('section_canonical_name'), [this.boolField('cardSplitCanonicalName', 'split_label'), this.intField('cardCanonicalNameX', 'x_label'), this.intField('cardCanonicalNameY', 'y_label'), this.intField('cardCanonicalNameSize', 'size_label'), this.fontFields('cardCanonicalNameFont', 'cardCanonicalNameFontStyle')]), this.group(t('section_author'), [this.intField('cardAuthorX', 'x_label'), this.intField('cardAuthorY', 'y_label'), this.intField('cardAuthorSize', 'size_label'), this.fontFields('cardAuthorFont', 'cardAuthorFontStyle')]), this.group(t('section_synonym'), [this.boolField('cardShowSynonym', 'show_label'), this.intField('cardSynonymX', 'x_label'), this.intField('cardSynonymY', 'y_label'), this.intField('cardSynonymSize', 'size_label'), this.fontFields('cardSynonymFont', 'cardSynonymFontStyle')]), this.group(t('section_vernacular_name'), [this.intField('cardVernacularNameX', 'x_label'), this.intField('cardVernacularNameY', 'y_label'), this.intField('cardVernacularNameSize', 'size_label'), this.fontFields('cardVernacularNameFont', 'cardVernacularNameFontStyle')]), this.group(t('section_toxicity_text'), [this.boolField('cardShowToxicityText', 'show_label'), this.intField('cardToxicityTextX', 'x_label'), this.intField('cardToxicityTextY', 'y_label'), this.intField('cardToxicityTextSize', 'size_label'), this.fontFields('cardToxicityTextFont', 'cardToxicityTextFontStyle')]), this.group(t('section_toxicity_logo'), [this.boolField('cardShowToxicityLogo', 'show_label'), this.intField('cardToxicityLogoWidth', 'width_label'), this.intField('cardToxicityLogoHeight', 'height_label'), this.intField('cardToxicityLogoX', 'x_label'), this.intField('cardToxicityLogoY', 'y_label')]), this.group(t('section_qr_code'), [this.boolField('cardShowQrCode', 'show_label'), this.intField('cardQrCodeSize', 'qr_code_size_label'), this.intField('cardQrCodeX', 'x_label'), this.intField('cardQrCodeY', 'y_label'), this.qrHidesField()]), this.group(t('section_numbering'), [this.boolField('cardWithNo', 'with_no_label')]), this.group(t('section_order'), [this.boolField('cartothequeSortByPhylum', 'sort_by_phylum_label')]), m("div", {
      className: "ChampisCardLayoutEditor-footer"
    }, this.saved && m("span", {
      className: "ChampisCardLayoutEditor-savedNotice"
    }, t('saved_notice')), m(Button, {
      className: "Button",
      onclick: () => this.cancel(),
      disabled: this.loading
    }, t('cancel_button')), m(Button, {
      className: "Button Button--primary",
      onclick: () => this.submit(),
      loading: this.loading,
      disabled: !this.canSubmit()
    }, t('save_button')))), m("div", {
      className: "ChampisCardLayoutEditor-previewColumn"
    }, m("div", {
      className: "ChampisCardLayoutEditor-previewHeader"
    }, m("label", null, t('preview_sample_label'), m("select", {
      className: "FormControl",
      value: this.previewEntryId || '',
      onchange: e => {
        this.previewEntryId = e.target.value || null;
      }
    }, m("option", {
      value: ""
    }, t('preview_sample_fictional')), this.entries && this.entries.map(entry => m("option", {
      value: entry.id()
    }, entry.no() ? `${entry.no()} — ` : '', this.entryLabel(entry))))), this.previewEntryId && m("a", {
      className: "Button ChampisCardLayoutEditor-downloadPdf",
      href: `${app.forum.attribute('apiUrl')}/champis-exhibitions-cartotheque-entries/${this.previewEntryId}/pdf`,
      target: "_blank",
      rel: "noopener noreferrer",
      onclick: e => {
        // Same cache-busting reasoning as CartothequePage's own
        // per-entry PDF download - a saved layout change
        // followed by re-downloading the same URL could
        // otherwise show a stale cached PDF from before the edit.
        e.currentTarget.href = `${app.forum.attribute('apiUrl')}/champis-exhibitions-cartotheque-entries/${this.previewEntryId}/pdf?_=${Date.now()}`;
      }
    }, m("i", {
      className: "icon fas fa-file-pdf",
      "aria-hidden": "true"
    }), " ", t('download_real_pdf_button'))), this.preview(), m("p", {
      className: "ChampisCardLayoutEditor-previewHint"
    }, t('preview_hint')))));
  }
  entryLabel(entry) {
    const display = entry.speciesDisplay();
    return display ? display.canonicalName || display.scientificName : entry.vernacularName() || `#${entry.id()}`;
  }
  sampleFor() {
    const entry = this.previewEntryId && this.entries ? this.entries.find(e => e.id() === this.previewEntryId) : null;
    if (!entry) return FICTIONAL_SAMPLE;
    const display = entry.speciesDisplay();
    const synonym = entry.synonymDisplay();
    const toxicity = entry.toxicity();
    return {
      no: entry.no() || FICTIONAL_SAMPLE.no,
      canonicalName: display && display.canonicalName || FICTIONAL_SAMPLE.canonicalName,
      author: display && display.author || '',
      synonymName: synonym ? synonym.canonicalName : null,
      family: display && display.family || FICTIONAL_SAMPLE.family,
      phylum: display && display.phylum || FICTIONAL_SAMPLE.phylum,
      vernacularName: entry.vernacularName() || '',
      toxicityName: toxicity ? toxicity.name() : null,
      toxicitySvgUrl: toxicity ? toxicity.svgUrl() : null,
      // Fictional sample: pretend a description exists, same reasoning as
      // every other FICTIONAL_SAMPLE field, so cardShowQrCode alone is
      // enough to see the placeholder without picking a real card first.
      hasDescription: !!entry.description()
    };
  }
  preview() {
    const f = this.fields;
    const sample = this.sampleFor();
    // Same $qrActive/$hidden() gating as CardPdfBuilder::build() - only
    // suppress an element once the QR code is actually going to show on
    // this sample.
    const qrActive = f.cardShowQrCode && sample.hasDescription;
    const hidden = key => qrActive && f.cardQrCodeHides === key;
    return m("div", {
      className: "ChampisCardLayoutEditor-cardWrap"
    }, m("div", {
      className: "ChampisCardLayoutEditor-card",
      style: {
        width: `${f.cardWidth}mm`,
        height: `${f.cardHeight}mm`
      }
    }, f.cardWithNo && !hidden('no') && m("div", {
      className: "ChampisCardLayoutEditor-cardNo",
      style: {
        left: '1.5mm',
        top: '1.5mm'
      }
    }, sample.no), f.cardShowToxicityLogo && !hidden('toxicity_logo') && sample.toxicityName && (sample.toxicitySvgUrl ? m("img", {
      className: "ChampisCardLayoutEditor-cardLogo",
      src: sample.toxicitySvgUrl,
      alt: "",
      style: {
        left: `${f.cardToxicityLogoX}mm`,
        top: `${f.cardToxicityLogoY}mm`,
        width: `${f.cardToxicityLogoWidth}mm`,
        height: `${f.cardToxicityLogoHeight}mm`
      }
    }) :
    // Fictional sample / a real toxicity with no SVG uploaded yet
    // - nothing real to show, so a placeholder box at the same
    // position/size still lets the position/size fields be
    // judged visually.
    m("div", {
      className: "ChampisCardLayoutEditor-cardToxicityLogo",
      style: {
        left: `${f.cardToxicityLogoX}mm`,
        top: `${f.cardToxicityLogoY}mm`,
        width: `${f.cardToxicityLogoWidth}mm`,
        height: `${f.cardToxicityLogoHeight}mm`
      }
    }, m("i", {
      className: "icon fas fa-skull-crossbones",
      "aria-hidden": "true"
    }))), f.cardShowToxicityText && !hidden('toxicity_text') && sample.toxicityName && m("div", {
      className: "ChampisCardLayoutEditor-cardText",
      style: this.textStyle(f, 'cardToxicityText', f.cardToxicityTextX, f.cardToxicityTextY)
    }, sample.toxicityName), f.cardLogoUrl && !hidden('logo') && m("img", {
      className: "ChampisCardLayoutEditor-cardLogo",
      src: f.cardLogoUrl,
      alt: "",
      style: {
        left: `${f.cardLogoX}mm`,
        top: `${f.cardLogoY}mm`,
        width: `${f.cardLogoWidth}mm`,
        height: `${f.cardLogoHeight}mm`
      }
    }), f.cardSocietyName && !hidden('society_name') && m("div", {
      className: "ChampisCardLayoutEditor-cardText",
      style: this.textStyle(f, 'cardSocietyName', f.cardSocietyNameX, f.cardSocietyNameY)
    }, f.cardSocietyName), f.cardShowFamily && !hidden('family') && sample.family && m("div", {
      className: "ChampisCardLayoutEditor-cardText",
      style: {
        ...this.textStyle(f, 'cardFamily', f.cardFamilyX, f.cardFamilyY),
        width: `calc(${f.cardWidth}mm - ${2 * Number(f.cardFamilyX)}mm)`
      }
    }, `FAM. ${sample.family.toUpperCase()}`), f.cardShowPhylum && !hidden('phylum') && sample.phylum && m("div", {
      className: "ChampisCardLayoutEditor-cardText",
      style: {
        ...this.textStyle(f, 'cardPhylum', f.cardPhylumX, f.cardPhylumY),
        width: `calc(${f.cardWidth}mm - ${2 * Number(f.cardPhylumX)}mm)`
      }
    }, phylumLabel(sample.phylum)), sample.canonicalName && !hidden('species_name') && m("div", {
      className: "ChampisCardLayoutEditor-cardText ChampisCardLayoutEditor-cardCanonicalName",
      style: {
        ...this.textStyle(f, 'cardCanonicalName', f.cardCanonicalNameX, f.cardCanonicalNameY),
        width: `calc(${f.cardWidth}mm - ${2 * Number(f.cardCanonicalNameX)}mm)`
      }
    }, f.cardSplitCanonicalName ? (() => {
      const [genus, ...rest] = sample.canonicalName.split(' ');
      return m('[', null, m("div", null, genus), m("div", null, rest.join(' ')));
    })() : sample.canonicalName), sample.canonicalName && sample.author && !hidden('species_name') && m("div", {
      className: "ChampisCardLayoutEditor-cardText",
      style: this.textStyle(f, 'cardAuthor', f.cardAuthorX, f.cardAuthorY)
    }, sample.author), f.cardShowSynonym && sample.synonymName && !hidden('synonym') && m("div", {
      className: "ChampisCardLayoutEditor-cardText",
      style: {
        ...this.textStyle(f, 'cardSynonym', f.cardSynonymX, f.cardSynonymY),
        fontStyle: 'normal',
        width: `calc(${f.cardWidth}mm - ${2 * Number(f.cardSynonymX)}mm)`
      }
    }, "(=", String(f.cardSynonymFontStyle).includes('I') ? m("i", null, sample.synonymName) : sample.synonymName, ")"), sample.vernacularName && !hidden('vernacular_name') && m("div", {
      className: "ChampisCardLayoutEditor-cardText",
      style: this.textStyle(f, 'cardVernacularName', f.cardVernacularNameX, f.cardVernacularNameY)
    }, sample.vernacularName), f.cardShowQrCode && sample.hasDescription && m("div", {
      className: "ChampisCardLayoutEditor-cardQrCode",
      style: {
        left: `${f.cardQrCodeX}mm`,
        top: `${f.cardQrCodeY}mm`,
        width: `${f.cardQrCodeSize}mm`,
        height: `${f.cardQrCodeSize}mm`
      }
    }, m("i", {
      className: "icon fas fa-qrcode",
      "aria-hidden": "true"
    }))));
  }

  // Builds the {left, top, fontFamily, fontWeight, fontStyle, fontSize}
  // style object shared by every positioned text element above - `prefix`
  // is the CARD_ATTRS group name (e.g. 'cardSocietyName') so this can pull
  // ...Font/...FontStyle/...Size off this.fields itself rather than each
  // call site repeating that 3-field lookup by hand.
  // x/y (and every f[...] lookup below) take/read CardFieldValue, not a
  // narrower number/string - coerced once here via Number()/String() rather
  // than requiring every call site (there are many, throughout preview()) to
  // do it themselves first.
  textStyle(f, prefix, x, y) {
    const style = String(f[`${prefix}FontStyle`] ?? '');
    return {
      left: `${Number(x)}mm`,
      top: `${Number(y)}mm`,
      fontFamily: FONT_STACKS[String(f[`${prefix}Font`] ?? '')] || FONT_STACKS.helvetica,
      fontWeight: style.includes('B') ? 'bold' : 'normal',
      fontStyle: style.includes('I') ? 'italic' : 'normal',
      fontSize: `${Number(f[`${prefix}Size`])}pt`
    };
  }
  group(title, fields) {
    return m("fieldset", {
      className: "ChampisCardLayoutEditor-group"
    }, m("legend", null, title), m("div", {
      className: "ChampisCardLayoutEditor-groupFields"
    }, fields));
  }
  intField(key, labelKey) {
    return m("div", {
      key: key,
      className: "ChampisCardLayoutEditor-field ChampisCardLayoutEditor-field--number"
    }, m("label", null, t(labelKey), m("input", {
      className: "FormControl",
      type: "number",
      value: this.fields[key],
      oninput: e => {
        this.fields[key] = parseInt(e.target.value, 10) || 0;
        this.saved = false;
      }
    })));
  }
  textField(key, labelKey, placeholderKey) {
    return m("div", {
      key: key,
      className: "ChampisCardLayoutEditor-field ChampisCardLayoutEditor-field--text"
    }, m("label", null, t(labelKey), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: t(placeholderKey),
      value: this.fields[key] || '',
      oninput: e => {
        this.fields[key] = e.target.value;
        this.saved = false;
      }
    })));
  }
  boolField(key, labelKey) {
    return m("div", {
      key: key,
      className: "ChampisCardLayoutEditor-field ChampisCardLayoutEditor-field--bool"
    }, m("label", null, m("input", {
      type: "checkbox",
      checked: this.fields[key],
      onchange: e => {
        this.fields[key] = e.target.checked;
        this.saved = false;
      }
    }), ' ', t(labelKey)));
  }
  fontFields(fontKey, styleKey) {
    return m("div", {
      key: fontKey,
      className: "ChampisCardLayoutEditor-fontFields"
    }, m("div", {
      className: "ChampisCardLayoutEditor-field"
    }, m("label", null, t('font_label'), m("select", {
      className: "FormControl",
      value: this.fields[fontKey],
      onchange: e => {
        this.fields[fontKey] = e.target.value;
        this.saved = false;
      }
    }, FONTS.map(font => m("option", {
      value: font
    }, t(`font_${font}`)))))), m("div", {
      className: "ChampisCardLayoutEditor-field"
    }, m("label", null, t('font_style_label'), m("select", {
      className: "FormControl",
      value: this.fields[styleKey],
      onchange: e => {
        this.fields[styleKey] = e.target.value;
        this.saved = false;
      }
    }, FONT_STYLES.map(style => m("option", {
      value: style
    }, t(`font_style_${this.fontStyleKey(style)}`)))))));
  }
  fontStyleKey(style) {
    return {
      '': 'normal',
      B: 'bold',
      I: 'italic',
      BI: 'bold_italic'
    }[style];
  }
  qrHidesField() {
    return m("div", {
      key: "cardQrCodeHides",
      className: "ChampisCardLayoutEditor-field"
    }, m("label", null, t('qr_code_hides_label'), m("select", {
      className: "FormControl",
      value: this.fields.cardQrCodeHides || '',
      onchange: e => {
        this.fields.cardQrCodeHides = e.target.value || null;
        this.saved = false;
      }
    }, m("option", {
      value: ""
    }, t('qr_code_hides_none')), QR_HIDABLE_ELEMENTS.map(_ref2 => {
      let [key, labelKey] = _ref2;
      return m("option", {
        value: key
      }, t(labelKey));
    }))));
  }
  logoField() {
    return m("div", {
      key: "cardLogoUrl",
      className: "ChampisCardLayoutEditor-field"
    }, m("label", null, t('logo_label')), this.fields.cardLogoUrl ? m("div", {
      className: "ChampisCardLayoutEditor-logo"
    }, m("img", {
      className: "ChampisCardLayoutEditor-logoThumb",
      src: this.fields.cardLogoUrl,
      alt: ""
    }), m(Button, {
      className: "Button Button--link",
      onclick: () => {
        this.fields.cardLogoUrl = null;
        this.saved = false;
      }
    }, t('logo_remove'))) : m("div", {
      className: "ChampisCardLayoutEditor-logoUpload"
    }, m(FileButton, {
      accept: "image/svg+xml",
      icon: "fas fa-upload",
      disabled: this.logoUploading,
      onfile: file => this.uploadLogo(file)
    }, t('logo_choose_button')), this.logoUploading && m("span", {
      className: "ChampisCardLayoutEditor-uploading"
    }, t('logo_uploading'))));
  }
  uploadLogo(file) {
    if (!file) return;
    this.logoUploading = true;
    m.redraw();
    uploadFile(file).then(uploaded => {
      this.logoUploading = false;
      if (uploaded) {
        this.fields.cardLogoUrl = uploaded.attributes.url;
        this.saved = false;
      }
      m.redraw();
    }).catch(error => {
      this.logoUploading = false;
      this.errorMessage = error.response && error.response.errors && error.response.errors[0] && error.response.errors[0].detail || app.translator.trans('champis-exhibitions.forum.card_layout_editor.error');
      m.redraw();
    });
  }
  canSubmit() {
    return !this.loading && !this.logoUploading;
  }
  cancel() {
    this.resetFields();
    this.errorMessage = null;
    this.saved = false;
  }
  submit() {
    this.loading = true;
    this.errorMessage = null;
    this.saved = false;
    m.redraw();
    const attributes = {
      ...this.fields
    };
    attributes.cardSocietyName = String(attributes.cardSocietyName || '').trim() || null;
    this.attrs.society.save(attributes).then(() => {
      this.loading = false;
      this.saved = true;
      m.redraw();
      if (this.attrs.onsaved) this.attrs.onsaved();
    }).catch(error => {
      this.loading = false;
      this.errorMessage = error && error.message || app.translator.trans('champis-exhibitions.forum.card_layout_editor.error');
      m.redraw();
    });
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/CardLayoutEditor', CardLayoutEditor);